/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _5 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Переменные</h2>
    <p className={styles.description}>
      Переменные используются для хранения данных и состоят из идентификатора (имени) и области в памяти, где хранится
      их значение. Переменную можно представить как коробку с названием, в которой что-то лежит (значение).
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Имена переменных</h3>
      <p className={styles.description}>
        <b>Идентификатор</b> — это имя переменной, функции, параметра или класса. Состоит из одного или нескольких
        символов в следующем формате:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Первым символом должна быть буква <code className={styles.code}>a-z</code> или{' '}
          <code className={styles.code}>A-Z</code>, символ подчеркивания <code className={styles.code}>_</code>
          или знак доллара <code className={styles.code}>$</code>.
        </li>
        <li className={styles.list__item}>
          Другие символы могут быть буквами <code className={styles.code}>a-z</code>,{' '}
          <code className={styles.code}>A-Z</code>, цифрами <code className={styles.code}>0-9</code>, подчеркиваниями
          <code className={styles.code}>_</code> и знаками доллара <code className={styles.code}>$</code>.
        </li>
        <li className={styles.list__item}>
          Все идентификаторы чувствительны к регистру. Это значит, что переменные
          <code className={styles.code}>user</code>, <code className={styles.code}>usEr</code> и{' '}
          <code className={styles.code}>User</code> различны.
        </li>
      </ul>
      <p className={styles.description}>Имя переменной должно быть понятным.</p>
      <CopyBlock
        text={`❌ Плохо
chislo   korzina_tovarov   profil_polzovatelya   tekushiy_mesyaz

✅ Хорошо
number   cart    userProfile     currentMonth`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Использование camelCase-нотации для идентификаторов это стандарт. При таком подходе, первое слово пишется
        строчными буквами, а каждое дополнительное начинается с прописной буквы. К примеру:{' '}
        <code className={styles.code}>user</code>, <code className={styles.code}>greetUser</code>,{' '}
        <code className={styles.code}>getUserData</code>,<code className={styles.code}>isActive</code>,{' '}
        <code className={styles.code}>activeGuestCount</code>, <code className={styles.code}>totalWorkerSalary</code>.
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            rel="noopener noreferrer"
            href="https://bookflow.ru/kak-nazyvat-peremennye/"
            target="_blank"
          >
            Как называть переменные
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            rel="noopener noreferrer"
            href="https://medium.freecodecamp.org/javascript-naming-conventions-dos-and-don-ts-99c0e2fdd78a"
            target="_blank"
          >
            JavaScript naming conventions
          </a>
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.2 - Ключевые слова</h3>
      <p className={styles.description}>
        Есть список зарезервированных ключевых слов, которые имеют специальное значение и используются для определенных
        конструкций. Нельзя использовать ключевые слова как идентификаторы.
      </p>
      <a
        className={styles.link}
        rel="noopener noreferrer"
        href="https://www.w3schools.com/js/js_reserved.asp"
        target="_blank"
      >
        Таблица зарезервированных слов
      </a>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.3 - Объявление переменных</h3>
      <p className={styles.description}>В современном JavaScript есть 2 ключевых слова для объявления переменных.</p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>let</code> — объявляет переменную, которой можно присвоить новое значение после
          объявления, то есть переопределить.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>const</code> — так же объявляет переменную, но с одним отличием: объявляемая
          переменная должна быть немедленно инициализирована значением и этой переменной не может быть присвоено другое
          значение после инициализации.
        </li>
      </ul>
      <p className={styles.notificationError}>
        Создание переменной без ключевого слова <code className={styles.codeError}>let</code> или{' '}
        <code className={styles.codeError}>const</code> приведет к ошибке.
      </p>
      <CopyBlock
        text={`/*
 * Для объявления переменной используются ключевые слова let и const,
 * за которыми следует имя переменной.
 * Переменным объявленным через let не обязательно сразу присваивать значение.
 */
let age;

/*
 * Переменные объявленные как const обязательно должны быть инициализированы
 * значением во время объявления, иначе будет ошибка.
 */
const yearOfBirth = 2006;

/*
 * Значение переменной можно получить обратившись к ней по имени.
 * console.log() используется для вывода данных в консоль разработчика,
 * с этим методом более детально познакомимся позже.
 */
console.log(yearOfBirth); // 15

/*
 * Если переменной объявленной как let, изначально не присвоено значение,
 * она инициализируется специальным значением undefined (не определено).
 */
console.log(age); // undefined

/*
 * Если переменная объявлена как let, ее значение можно перезаписать.
 */
age = 14;
console.log(age); // 14

/*
 * Если переменная объявлена как const, перезаписать ее значение нельзя.
 * При попытке присвоить новое значение будешь ошибка выполнения скрипта.
 */

yearOfBirth = 2020; // будет ошибка
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.4 - Когда использовать const и let</h3>
      <p className={styles.description}>
        Единственное отличие <code className={styles.code}>const</code> и <code className={styles.code}>let</code>{' '}
        состоит в том, что <code className={styles.code}>const</code> запрещает повторное присваивание переменной
        какого-либо значения. Объявление <code className={styles.code}>const</code>
        делает код более читаемым, так как переменная всегда ссылается на одно и то же значение. В случае с{' '}
        <code className={styles.code}>let</code> такой уверенности нет.
      </p>
      <p className={styles.description}>
        Будет разумно использовать <code className={styles.code}>let</code> и <code className={styles.code}>const</code>{' '}
        так:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Используйте <code className={styles.code}>const</code> по умолчанию, большинство переменных будут объявлены
          именно так.
        </li>
        <li className={styles.list__item}>
          Используйте <code className={styles.code}>let</code>, если потребуется присвоить переменной другое значение во
          время выполнения скрипта.
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.5 - константы и КОНСТАНТЫ</h3>
      <p className={styles.description}>
        Имена КОНСТАНТ - переменных, значение которых не изменяется никогда на протяжении работы всего скрипта, обычно
        записываются в формате
        <code className={styles.code}>UPPER_SNAKE_CASE</code>.
      </p>
      <CopyBlock
        text={`// Константа хранящая значение цвета
const COLOR_TEAL = '#009688';

// Константа хранящая сообщение о авторизации
const MESSAGE_LOGIN_SUCCESS = 'Добро пожаловать!';`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Абсолютное большинство переменных – константы в другом смысле, они просто не изменяют значение после присвоения.
        Но при разных запусках скрипта, это значение может быть разным. Имена таких переменных записывают используя
        формат
        <code className={styles.code}>camelCase</code>.
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.6 - Обращение к переменной</h3>
    </section>
    <p className={styles.description}>Важно различать неопределенные и необъявленные переменные.</p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        <b>Неопределенная (undefined)</b> — это переменная, которая была объявлена, но не инициализирована значением, по
        умолчанию ей присваивается значение <code className={styles.code}>undefined</code>
        как начальное.
      </li>
      <li className={styles.list__item}>
        <b>Необъявленная (undeclared или not defined)</b> — это переменная, которая не была объявлена в доступной
        области видимости.
      </li>
    </ul>
    <CopyBlock
      text={`let name;

/*
 * Переменная name объявлена, но не инициализирована, поэтому
 * ее значение undefined (не определено), тогда как переменная age
 * не была объявлена, поэтому доступ к ней вызывает ошибку ReferenceError
 */
console.log(name); // undefined
console.log(age); // ReferenceError: age is not defined`}
      language="js"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>Попытка обратиться к пременной до ее объявления вызовет ошибку.</p>{' '}
    <CopyBlock
      text={`// console.log(a); // ReferenceError: a is not defined
let a = 10;
console.log(a); // 10

// console.log(b); // ReferenceError: b is not defined
const b = 15;
console.log(b); //15`}
      language="js"
      showLineNumbers={false}
      theme={ocean}
    />
    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Типы примитивов</h2>
      <p className={styles.description}>
        JavaScript это динамический, слабо типизированный язык, поэтому переменная не ассоциируется с каким-либо типом
        данных, тип есть у значения переменной. То есть переменная может хранить значения разных типов.
      </p>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.1 - Number</h3>
        <p className={styles.description}>
          Целые числа и числа с плавающей запятой. После объявления переменной, можно инициализировать ее числовым
          значением.
        </p>
        <CopyBlock
          text={`const age = 20;
const number = 5.8;`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.2 - String</h3>
        <p className={styles.description}>
          Строки - это просто текст, последовательность из нуля или более символов. Строка начинается и заканчивается
          одиночной <code className={styles.code}>'</code> или двойными кавычками <code className={styles.code}>"</code>
          . Открывающая и закрывающая кавычки должны быть одинаковые.
        </p>
        <CopyBlock text={`const name = 'Mango';`} language="js" showLineNumbers={false} theme={ocean} />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.3 - Boolean</h3>
        <p className={styles.description}>
          Логический тип данных, флаги состояния. Имеет только два значения: <code className={styles.code}>true</code> и
          <code className={styles.code}>false</code>, в нижнем регистре. Например на вопрос включен ли свет в комнате
          можно ответить да (<code className={styles.code}>true</code>) или нет (
          <code className={styles.code}>false</code>).
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>true</code> — да, верно, истина, 1
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>false</code> — нет, неверно, ложь, 0
          </li>
        </ul>
        <p className={styles.description}>
          Обратите внимание на имена переменных содержащих буль. Они задают вопрос, и ответ на его — да или нет.
        </p>
        <CopyBlock
          text={`const isAuthenticated = true;
const canMerge = false;
const hasChildren = true;
const isModalOpen = false;`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.4 - String</h3>
        <p className={styles.description}>
          Особое значение, которое по сути значит <code className={styles.code}>ничто</code>. Используется в тех
          ситуациях, когда необходимо явно указать отсутсвие значения. К примеру если в базе данных не нашли
          пользователя, то можно сказать что значение <code className={styles.code}>null</code>.
        </p>
        <CopyBlock
          text={`// Переменная guest пуста и лишена значения
const guest = null;`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.5 - undefined</h3>
        <p className={styles.description}>
          Еще одно специальное значение. По умолчанию, когда переменная объявляется, но не инициализируется, ее значение
          не определено, ей присваивается <code className={styles.code}>undefined</code>.
        </p>
        <CopyBlock
          text={`let name;
console.log(name); // undefined`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.6 - Оператор typeof</h3>
        <p className={styles.description}>
          Для получения типа значения переменной, используется оператор <code className={styles.code}>typeof</code>,
          который возвращает на место своего вызова тип значения переменной указанного после него. Возвращаемое значение
          это просто строка в которой указан тип.
        </p>
        <CopyBlock
          text={`// undefined
let a;
console.log(typeof a); // "undefined"

// null
const b = null;
console.log(typeof null); // "object"

// Number
const c = 5;
console.log(typeof c); // "number"

// String
const d = 'JavaScript is awesome!';
console.log(typeof d); // "string"

// Boolean
const e = false;
console.log(typeof e); // "boolean"`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
  </section>
))
